import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { ChevronRight } from "@/components/icons";
import { Button, Input, Tabs, TabsContent, toast } from "@/components/kit";
import type { CategoryDTO } from "@/modules/products/domain/contracts";
import type { ProductListItemDTO } from "@/modules/products/domain/types";
import {
  useProductCategoriesQuery,
  useProductsQuery,
} from "@/modules/products/presentation/api/queries";
import type {
  ProfitDateBasis,
  ReportDateBasis,
} from "@/modules/reports/domain/constants";
import type {
  ReportProductFilterDTO,
  StockMovementsQueryDTO,
} from "@/modules/reports/domain/contracts";
import type { ReportDateRangeState } from "@/modules/reports/domain/date-range-presets";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { reportsApi } from "../api/client";
import {
  useAgingStockQuery,
  useCustomJobsSummaryQuery,
  useCustomerCohortsQuery,
  useCustomerProductAffinityQuery,
  useCustomerReportByDistrictQuery,
  useCustomerReportByProvinceQuery,
  useCustomerReportSummaryQuery,
  useCustomerReportTopQuery,
  useCustomerRiskSummaryQuery,
  useCustomerWatchlistQuery,
  useDeadStockQuery,
  useDeadStockSettingsQuery,
  useMarketingRoiQuery,
  useProductPerformanceQuery,
  useProfitByDayQuery,
  useProfitByProductQuery,
  useProfitCampaignsQuery,
  useProfitSummaryQuery,
  useSalesByDayQuery,
  useSalesFrequencyQuery,
  useSalesSummaryQuery,
  useShippingPerformanceQuery,
  useStockMovementsQuery,
  useStockValuationQuery,
  useUpdateDeadStockSettingsMutation,
} from "../api/queries";
import type { ReportDatePreset } from "../lib/date-range";
import type { ReportsSavedView } from "../lib/reports-saved-views";
import {
  customerSectionFromSearch,
  productFilterFromSearch,
  profitSectionFromSearch,
  rangeFromReportsSearch,
  reportsUsesDateRange,
  salesSectionFromSearch,
  stockSectionFromSearch,
  type CustomerSection,
  type CustomerWatchlistKind,
  type ProductSection,
  type ReportsSearch,
} from "../lib/reports-search";
import { AgingStockTable } from "../ui/AgingStockTable";
import { CustomJobsReportPanel } from "../ui/CustomJobsReportPanel";
import { CustomerAffinityChart } from "../ui/CustomerAffinityChart";
import { CustomerAffinityTable } from "../ui/CustomerAffinityTable";
import { CustomerCohortChart } from "../ui/CustomerCohortChart";
import { CustomerCohortTable } from "../ui/CustomerCohortTable";
import { CustomerGeoChart } from "../ui/CustomerGeoChart";
import { CustomerGeoTable } from "../ui/CustomerGeoTable";
import { CustomerReportsOverviewStrip } from "../ui/CustomerReportsOverviewStrip";
import { CustomerRiskTierChart } from "../ui/CustomerRiskTierChart";
import { CustomerTopTable } from "../ui/CustomerTopTable";
import { CustomerWatchlistTable } from "../ui/CustomerWatchlistTable";
import { DeadStockSettingsPanel } from "../ui/DeadStockSettingsPanel";
import { DeadStockTable } from "../ui/DeadStockTable";
import { MarketingRoiTable } from "../ui/MarketingRoiTable";
import { ProductPerformanceTable } from "../ui/ProductPerformanceTable";
import { ProfitByProductTable } from "../ui/ProfitByProductTable";
import { ProfitCampaignSummaryTable } from "../ui/ProfitCampaignSummaryTable";
import { ProfitChart } from "../ui/ProfitChart";
import { ProfitExpenseBreakdown } from "../ui/ProfitExpenseBreakdown";
import { ProfitSummaryCards } from "../ui/ProfitSummaryCards";
import { ReportShowMore } from "../ui/ReportShowMore";
import { ReportsFilterBar } from "../ui/ReportsFilterBar";
import { ReportsQueryError } from "../ui/ReportsQueryError";
import { ReportsSectionNav } from "../ui/ReportsSectionNav";
import { SalesChart } from "../ui/SalesChart";
import { SalesFrequencyTable } from "../ui/SalesFrequencyTable";
import { SalesSummaryCards } from "../ui/SalesSummaryCards";
import { ShippingOverviewStrip } from "../ui/ShippingOverviewStrip";
import { ShippingPerformanceTable } from "../ui/ShippingPerformanceTable";
import { StockMovementsTable } from "../ui/StockMovementsTable";
import { StockOverviewStrip } from "../ui/StockOverviewStrip";
import { StockValuationSummaryCards } from "../ui/StockValuationSummaryCards";
import { StockValuationTable } from "../ui/StockValuationTable";

const STOCK_PAGE_SIZE = 20;
const ALL_MARKETING_FILTER = "__all";

const REPORT_LIMITS = {
  product: { initial: 10, step: 20, max: 50 },
  frequency: { initial: 50, step: 50, max: 100 },
  profitProduct: { initial: 10, step: 20, max: 50 },
  marketingRoi: { initial: 20, step: 30, max: 100 },
  aging: { initial: 20, step: 30, max: 100 },
  valuation: { initial: 20, step: 30, max: 200 },
  deadStock: { initial: 50, step: 50, max: 200 },
  shipping: { initial: 50, step: 50, max: 100 },
  customerTop: { initial: 20, step: 20, max: 100 },
} as const;

function canShowMore(length: number, limit: number, max: number) {
  return length >= limit && limit < max;
}

const SALES_SECTIONS = [
  {
    value: "overview",
    label: "ພາບລວມ",
    description: "ຍອດຂາຍ ແລະ ກຣາຟຕາມວັນ",
  },
  {
    value: "products",
    label: "ສິນຄ້າ",
    description: "ຂາຍດີ, ຂາຍຊ້າ ແລະ ຄວາມຖີ່",
  },
] as const;

const PRODUCT_SECTIONS = [
  { value: "best", label: "ຂາຍດີ", description: "ອັນດັບຕາມຍອດຂາຍ" },
  {
    value: "worst",
    label: "ຂາຍຊ້າ",
    description: "ມີສະຕັອກແຕ່ຂາຍໜ້ອຍ",
  },
  {
    value: "frequency",
    label: "ຄວາມຖີ່",
    description: "ຈຳນວນຄັ້ງ ແລະ ຂາຍລ່າສຸດ",
  },
] as const;

const PROFIT_SECTIONS = [
  {
    value: "overview",
    label: "ພາບລວມ",
    description: "KPI, ລາຍຈ່າຍ ແລະ ກຣາຟກຳໄລ",
  },
  {
    value: "marketing",
    label: "ການຕລາດ",
    description: "ຄ່າແອດ, ROAS ແລະ campaign",
  },
  {
    value: "products",
    label: "ສິນຄ້າ",
    description: "ກຳໄລແຍກຕາມສິນຄ້າ",
  },
] as const;

const STOCK_SECTIONS = [
  {
    value: "valuation",
    label: "ມູນຄ່າຄລັງ",
    description: "ຄົງເຫລຼືອ + ລະຫວ່າງທາງ",
  },
  {
    value: "aging",
    label: "ຄ້າງສະຕັອກ",
    description: "lot ເກົ່າທີ່ຍັງຄ້າງ",
  },
  {
    value: "dead",
    label: "ສິນຄ້າຄ້າງ",
    description: "ບໍ່ຂາຍ / ຂາຍຊ້າ — ແນະນຳ action",
  },
  {
    value: "movements",
    label: "ປະຫວັດ",
    description: "ການເຄື່ອນໄຫວສະຕັອກ",
  },
] as const;

const CUSTOMER_SECTIONS = [
  {
    value: "overview",
    label: "ພາບລວມ",
    description: "KPI ແລະ ສະຫຼຸບລູກຄ້າ",
  },
  {
    value: "top",
    label: "Top",
    description: "ລູກຄ້າອັນດັບຕົ້ນຕາມຍອດ",
  },
  {
    value: "geo",
    label: "ແຂວງ",
    description: "ຍອດແຍກຕາມພື້ນທີ່",
  },
  {
    value: "watch",
    label: "ເຝົາລະວັງ",
    description: "COD ຕີກັບ ແລະ ລາຍການເສີ່ງ",
  },
  {
    value: "affinity",
    label: "ສິນຄ້າ",
    description: "ສິນຄ້າທີ່ລູກຄ້າຊື້ຊ້ຳຊອບ",
  },
  {
    value: "cohorts",
    label: "Cohort",
    description: "ອັດຕາກັບມາຊື້ຕາມເດືອນ",
  },
] as const;

const CUSTOMER_WATCH_KINDS = [
  {
    value: "cod_returns",
    minCodReturns: 1,
    label: "COD ຕີກັບ",
    description: "ມີ COD ຕີກັບ (ຕະຫຼອດ)",
  },
  {
    value: "cod_returns",
    minCodReturns: 2,
    label: "COD ≥2",
    description: "COD ຕີກັບ 2+ ຄັ້ງ",
  },
  {
    value: "inactive",
    minCodReturns: 1,
    label: "ບໍ່ມາຊື້",
    description: "ບໍ່ມີອໍເດີ້ 90+ ວັນ",
  },
  {
    value: "high_new",
    minCodReturns: 1,
    label: "ໃໝ່ + ຍອດສູງ",
    description: "ລູກຄ້າໃໝ່ຍອດ ≥ 500k",
  },
] as const satisfies ReadonlyArray<{
  value: CustomerWatchlistKind;
  minCodReturns: number;
  label: string;
  description: string;
}>;

const TAB_PAGE_META = {
  sales: {
    title: "ຍອດຂາຍ",
    description: "ຍອດຂາຍ, ອໍເດີ້ ແລະ ສິນຄ້າຂາຍດີ",
  },
  profit: {
    title: "ກຳໄລ",
    description: "ກຳໄລ, ລາຍຈ່າຍ ແລະ ROI ການຕລາດ",
  },
  stock: {
    title: "ສະຕັອກ",
    description: "ມູນຄ່າຄລັງ, ຄ້າງສະຕັອກ ແລະ ປະຫວັດ",
  },
  shipping: {
    title: "ຂົນສົ່ງ",
    description: "ອັດຕາຕີກັບ ແລະ ປະສິດທິພາບຂົນສົ່ງ",
  },
  customers: {
    title: "ລູກຄ້າ",
    description: "ລູກຄ້າໃໝ່, ຊື້ຊ້ຳ ແລະ ການແຈກຢາຍຕາມພື້ນທີ່",
  },
  custom_jobs: {
    title: "ງານສັ່ງເຮັດ",
    description: "ຍອດຂາຍ, COGS ແລະ ງານຄ້າງ",
  },
} as const;

export function ReportsPage() {
  const nav = useNavigate({ from: "/app/reports" });
  const routeSearch = useSearch({ from: "/app/reports" });

  // --- Navigation / section state (from URL) ---
  const activeTab = routeSearch.tab;
  const salesSection = salesSectionFromSearch(routeSearch);
  const productSection = routeSearch.sub ?? "best";
  const profitSection = profitSectionFromSearch(routeSearch);
  const stockSection = stockSectionFromSearch(routeSearch);
  const customerSection = customerSectionFromSearch(routeSearch);

  // --- Filter state derived from URL ---
  const range = rangeFromReportsSearch(routeSearch);
  const activePreset = routeSearch.preset;
  const profitBasis = routeSearch.profitBasis;
  const productFilter = productFilterFromSearch(routeSearch);
  const agingMinDays = routeSearch.agingMinDays;
  const stockReason = routeSearch.reason;
  const marketingPlatform = routeSearch.platform ?? "";
  const marketingCampaign = routeSearch.campaign ?? "";
  const marketingProductId = routeSearch.mappedProductId ?? "";
  const marketingCategoryId = routeSearch.categoryId ?? "";
  const customerProvince = routeSearch.customerProvince ?? "";
  const customerDistrict = routeSearch.customerDistrict ?? "";
  const watchKind = routeSearch.watchKind ?? "cod_returns";
  const minCodReturns = routeSearch.minCodReturns ?? 1;
  const cohortMonths = routeSearch.cohortMonths ?? 6;

  const activeWatchKindKey = `${watchKind}:${minCodReturns}`;

  // --- Local UI state ---
  const [stockOffset, setStockOffset] = useState(0);
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);

  useEffect(() => {
    setMoreFiltersOpen(false);
  }, [activeTab, stockSection]);
  const [productLimit, setProductLimit] = useState(
    REPORT_LIMITS.product.initial,
  );
  const [frequencyLimit, setFrequencyLimit] = useState(
    REPORT_LIMITS.frequency.initial,
  );
  const [profitProductLimit, setProfitProductLimit] = useState(
    REPORT_LIMITS.profitProduct.initial,
  );
  const [marketingRoiLimit, setMarketingRoiLimit] = useState(
    REPORT_LIMITS.marketingRoi.initial,
  );
  const [agingLimit, setAgingLimit] = useState(REPORT_LIMITS.aging.initial);
  const [valuationLimit, setValuationLimit] = useState(
    REPORT_LIMITS.valuation.initial,
  );
  const [deadStockLimit, setDeadStockLimit] = useState(
    REPORT_LIMITS.deadStock.initial,
  );
  const [shippingLimit, setShippingLimit] = useState(
    REPORT_LIMITS.shipping.initial,
  );
  const [customerTopLimit, setCustomerTopLimit] = useState(
    REPORT_LIMITS.customerTop.initial,
  );
  const [deadStockLookbackDays, setDeadStockLookbackDays] = useState<
    number | undefined
  >();
  const [deadStockMinDays, setDeadStockMinDays] = useState<
    number | undefined
  >();
  const [exporting, setExporting] = useState(false);
  const [deadStockExporting, setDeadStockExporting] = useState(false);

  // --- URL patch helper ---
  const updateSearch = (patch: Partial<ReportsSearch>) => {
    nav({
      search: {
        ...routeSearch,
        ...patch,
      },
    });
  };

  const handleSavedView = (view: ReportsSavedView) => {
    updateSearch({
      ...view.search,
      from: undefined,
      to: undefined,
      sub: undefined,
    });
    setStockOffset(0);
    setMoreFiltersOpen(false);
  };

  // --- Navigation handlers ---
  const handlePreset = (preset: ReportDatePreset) => {
    if (preset === "custom") {
      updateSearch({ preset: "custom" });
      return;
    }
    updateSearch({ preset, from: undefined, to: undefined });
    setStockOffset(0);
  };

  const handleCustomRange = (newRange: ReportDateRangeState) => {
    updateSearch({ preset: "custom", from: newRange.from, to: newRange.to });
    setStockOffset(0);
  };

  const handleBasis = (basis: ReportDateBasis) => {
    updateSearch({ basis });
    setStockOffset(0);
  };

  const handleProfitBasis = (pBasis: ProfitDateBasis) => {
    updateSearch({ profitBasis: pBasis });
  };

  const handleProductFilterChange = (next: ReportProductFilterDTO) => {
    updateSearch({ productId: next.productId, variantId: next.variantId });
    setStockOffset(0);
  };

  // --- Derived flags ---
  const salesActive = activeTab === "sales";
  const profitActive = activeTab === "profit";
  const stockActive = activeTab === "stock";
  const shippingActive = activeTab === "shipping";
  const customersActive = activeTab === "customers";
  const customJobsActive = activeTab === "custom_jobs";

  const showDateRange = reportsUsesDateRange(
    activeTab,
    stockSection,
    customerSection,
  );
  const showSalesBasis =
    showDateRange &&
    activeTab !== "profit" &&
    activeTab !== "customers" &&
    activeTab !== "custom_jobs";
  const showProfitBasis = profitActive;

  const showMoreFilters =
    profitActive ||
    (stockActive &&
      (stockSection === "movements" ||
        stockSection === "aging" ||
        stockSection === "dead"));

  const moreFiltersActive =
    !!(
      marketingPlatform ||
      marketingCampaign ||
      marketingProductId ||
      marketingCategoryId
    ) ||
    !!stockReason ||
    (stockActive && stockSection === "aging" && agingMinDays !== 30);

  // --- Dead stock settings ---
  const deadStockSettings = useDeadStockSettingsQuery({
    enabled: stockActive,
  });
  const resolvedDeadStockLookback =
    deadStockLookbackDays ?? deadStockSettings.data?.lookbackDays;
  const resolvedDeadStockMinDays =
    deadStockMinDays ?? deadStockSettings.data?.minDays;

  const updateDeadStockSettings = useUpdateDeadStockSettingsMutation();

  // --- Profit query object ---
  const profitQuery = {
    from: range.from,
    to: range.to,
    basis: profitBasis,
    platform: marketingPlatform.trim() || undefined,
    campaignName: marketingCampaign.trim() || undefined,
    mappedProductId: marketingProductId || undefined,
    mappedCategoryId: marketingCategoryId || undefined,
    ...productFilter,
  };

  // --- Data queries ---
  const summary = useSalesSummaryQuery(
    { ...range, ...productFilter },
    { enabled: salesActive && salesSection === "overview" },
  );
  const customJobsSummary = useCustomJobsSummaryQuery(
    { ...range, ...productFilter },
    { enabled: customJobsActive },
  );
  const byDay = useSalesByDayQuery(
    { ...range, ...productFilter },
    { enabled: salesActive && salesSection === "overview" },
  );
  const products = useProductPerformanceQuery(
    { ...range, limit: productLimit, sort: "best", ...productFilter },
    {
      enabled:
        salesActive && salesSection === "products" && productSection === "best",
    },
  );
  const slowProducts = useProductPerformanceQuery(
    { ...range, limit: productLimit, sort: "worst", ...productFilter },
    {
      enabled:
        salesActive &&
        salesSection === "products" &&
        productSection === "worst",
    },
  );
  const salesFrequency = useSalesFrequencyQuery(
    { ...range, limit: frequencyLimit, sort: "orderCount", ...productFilter },
    {
      enabled:
        salesActive &&
        salesSection === "products" &&
        productSection === "frequency",
    },
  );
  const agingStock = useAgingStockQuery(
    {
      minDays: agingMinDays,
      limit: stockSection === "aging" ? agingLimit : 50,
      ...productFilter,
    },
    { enabled: stockActive },
  );
  const stockValuation = useStockValuationQuery(
    {
      limit: stockSection === "valuation" ? valuationLimit : 5,
      ...productFilter,
    },
    { enabled: stockActive },
  );
  const deadStock = useDeadStockQuery(
    {
      lookbackDays: resolvedDeadStockLookback,
      minDays: resolvedDeadStockMinDays,
      limit: stockSection === "dead" ? deadStockLimit : 50,
      ...productFilter,
    },
    {
      enabled:
        stockActive &&
        resolvedDeadStockLookback != null &&
        resolvedDeadStockMinDays != null,
    },
  );
  const stock = useStockMovementsQuery(
    {
      from: range.from,
      to: range.to,
      reason: stockReason,
      limit: STOCK_PAGE_SIZE,
      offset: stockOffset,
      ...productFilter,
    },
    { enabled: stockActive && stockSection === "movements" },
  );

  const productsForMarketing = useProductsQuery({ includeInactive: true });
  const categoriesForMarketing = useProductCategoriesQuery();

  const profitSummary = useProfitSummaryQuery(profitQuery, {
    enabled:
      profitActive &&
      (profitSection === "overview" || profitSection === "marketing"),
  });
  const profitByDay = useProfitByDayQuery(profitQuery, {
    enabled: profitActive && profitSection === "overview",
  });
  const profitByProduct = useProfitByProductQuery(
    { ...profitQuery, limit: profitProductLimit },
    { enabled: profitActive && profitSection === "products" },
  );
  const profitCampaigns = useProfitCampaignsQuery(
    { ...profitQuery, limit: marketingRoiLimit },
    { enabled: profitActive && profitSection === "marketing" },
  );
  const marketingRoi = useMarketingRoiQuery(
    { ...profitQuery, limit: marketingRoiLimit },
    { enabled: profitActive && profitSection === "marketing" },
  );
  const shippingPerformance = useShippingPerformanceQuery(
    { ...range, limit: shippingLimit, ...productFilter },
    { enabled: shippingActive },
  );

  const customerReportQuery = {
    ...range,
    province: customerProvince || undefined,
    district: customerDistrict || undefined,
  };
  const customerSummary = useCustomerReportSummaryQuery(customerReportQuery, {
    enabled: customersActive && customerSection === "overview",
  });
  const customerTop = useCustomerReportTopQuery(
    { ...customerReportQuery, limit: customerTopLimit, sort: "revenue" },
    { enabled: customersActive && customerSection === "top" },
  );
  const customerGeoProvince = useCustomerReportByProvinceQuery(
    customerReportQuery,
    {
      enabled:
        customersActive && customerSection === "geo" && !customerProvince,
    },
  );
  const customerGeoDistrict = useCustomerReportByDistrictQuery(
    {
      ...customerReportQuery,
      province: customerProvince,
    },
    {
      enabled:
        customersActive && customerSection === "geo" && !!customerProvince,
    },
  );

  const customerRiskSummary = useCustomerRiskSummaryQuery(customerReportQuery, {
    enabled: customersActive && customerSection === "overview",
  });
  const customerWatchlist = useCustomerWatchlistQuery(
    {
      ...customerReportQuery,
      kind: watchKind,
      minCodReturns,
      codScope: watchKind === "cod_returns" ? "all_time" : undefined,
      limit: 50,
    },
    { enabled: customersActive && customerSection === "watch" },
  );
  const customerAffinity = useCustomerProductAffinityQuery(
    { ...customerReportQuery, limit: 15 },
    { enabled: customersActive && customerSection === "affinity" },
  );
  const customerCohorts = useCustomerCohortsQuery(
    {
      months: cohortMonths,
      maxOffset: 6,
      province: customerProvince || undefined,
      district: customerDistrict || undefined,
    },
    { enabled: customersActive && customerSection === "cohorts" },
  );

  const stockTotal = stock.data?.meta.total ?? 0;
  const stockHasMore = stockOffset + STOCK_PAGE_SIZE < stockTotal;
  const stockHasPrev = stockOffset > 0;

  const exportAvailable = !(stockActive && stockSection === "movements");

  const exportLabel = (() => {
    if (activeTab === "profit") return "CSV ກຳໄລ";
    if (activeTab === "shipping") return "CSV ຂົນສົ່ງ";
    if (activeTab === "customers") return "CSV ລູກຄ້າ";
    if (activeTab === "stock") {
      if (stockSection === "aging") return "CSV ຄ້າງສະຕັອກ";
      if (stockSection === "dead") return "CSV ສິນຄ້າຄ້າງ";
      if (stockSection === "valuation") return "CSV ມູນຄ່າຄລັງ";
    }
    if (activeTab === "sales" && salesSection === "products") {
      if (productSection === "frequency") return "CSV ຄວາມຖີ່";
      return "CSV ສິນຄ້າ";
    }
    return "ດາວໂຫລດ CSV";
  })();

  const handleExport = async () => {
    try {
      setExporting(true);
      if (activeTab === "profit") {
        await reportsApi.downloadProfitCsv(profitQuery);
      } else if (activeTab === "shipping") {
        await reportsApi.downloadShippingCsv({
          ...range,
          limit: shippingLimit,
          ...productFilter,
        });
      } else if (activeTab === "customers") {
        const exportSection =
          customerSection === "watch"
            ? "watch"
            : customerSection === "geo"
              ? "geo"
              : customerSection === "overview"
                ? "overview"
                : "top";
        await reportsApi.downloadCustomerCsv({
          ...customerReportQuery,
          exportSection,
          watchKind,
          limit: 500,
        });
      } else if (activeTab === "stock") {
        if (stockSection === "aging") {
          await reportsApi.downloadAgingStockCsv({
            minDays: agingMinDays,
            limit: agingLimit,
            ...productFilter,
          });
        } else if (
          stockSection === "dead" &&
          resolvedDeadStockLookback != null &&
          resolvedDeadStockMinDays != null
        ) {
          await reportsApi.downloadDeadStockCsv({
            lookbackDays: resolvedDeadStockLookback,
            minDays: resolvedDeadStockMinDays,
            limit: deadStockLimit,
            ...productFilter,
          });
        } else if (stockSection === "valuation") {
          await reportsApi.downloadStockValuationCsv({
            limit: valuationLimit,
            ...productFilter,
          });
        }
      } else if (activeTab === "sales" && salesSection === "products") {
        if (productSection === "frequency") {
          await reportsApi.downloadSalesFrequencyCsv({
            ...range,
            limit: frequencyLimit,
            sort: "orderCount",
            ...productFilter,
          });
        } else {
          await reportsApi.downloadProductPerformanceCsv({
            ...range,
            limit: productLimit,
            sort: productSection === "worst" ? "worst" : "best",
            ...productFilter,
          });
        }
      } else {
        await reportsApi.downloadOrdersCsv({ ...range, ...productFilter });
      }
      toast.success("ດາວໂຫລດ CSV ສຳເລັດ");
    } catch (e) {
      toast.error("ດາວໂຫລດລົ້ມເຫຼວ", {
        description: e instanceof Error ? e.message : undefined,
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <Header />
      <Main className="flex flex-col gap-6">
        <div className="flex flex-col gap-1">
          <h2 className="font-display text-2xl text-foreground sm:text-3xl">
            {TAB_PAGE_META[activeTab].title}
          </h2>
          <p className="text-muted-foreground text-sm">
            {TAB_PAGE_META[activeTab].description}
          </p>
          <div className="mt-2 h-0.5 w-16 rounded-full bg-secondary/70" />
        </div>

        <ReportsFilterBar
          showDateRange={showDateRange}
          showSalesBasis={showSalesBasis}
          showProfitBasis={showProfitBasis}
          showMoreFilters={showMoreFilters}
          range={range}
          activePreset={activePreset}
          profitBasis={profitBasis}
          productFilter={productFilter}
          moreFiltersOpen={moreFiltersOpen}
          moreFiltersActive={moreFiltersActive}
          exportAvailable={exportAvailable}
          exportLabel={exportLabel}
          exporting={exporting}
          onPresetChange={handlePreset}
          onBasisChange={handleBasis}
          onProfitBasisChange={handleProfitBasis}
          onCustomRangeChange={handleCustomRange}
          onProductFilterChange={handleProductFilterChange}
          onMoreFiltersOpenChange={setMoreFiltersOpen}
          onExport={handleExport}
          activeTab={activeTab}
          activeSection={
            activeTab === "sales"
              ? salesSection
              : activeTab === "profit"
                ? profitSection
                : activeTab === "stock"
                  ? stockSection
                  : activeTab === "customers"
                    ? customerSection
                    : activeTab === "custom_jobs"
                      ? "overview"
                      : "overview"
          }
          onSavedViewSelect={handleSavedView}
          moreFilters={
            <div className="flex flex-col gap-4">
              {profitActive ? (
                <>
                  <div>
                    <h3 className="font-display text-base">ຕົວກອງການຕລາດ</h3>
                    <p className="text-muted-foreground text-sm">
                      ກອງຄ່າແອດຕາມແພລດຟອມ, campaign ຫຼື mapping ທີ່ຜູກໄວ້
                    </p>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <Input
                      value={marketingPlatform}
                      onChange={(e) =>
                        updateSearch({
                          platform: e.target.value.trim() || undefined,
                        })
                      }
                      placeholder="ແພລດຟອມ: facebook"
                    />
                    <Input
                      value={marketingCampaign}
                      onChange={(e) =>
                        updateSearch({
                          campaign: e.target.value.trim() || undefined,
                        })
                      }
                      placeholder="ຊື່ campaign"
                    />
                    <SimpleSelect
                      value={marketingCategoryId || ALL_MARKETING_FILTER}
                      onValueChange={(value) =>
                        updateSearch({
                          categoryId:
                            value === ALL_MARKETING_FILTER ? undefined : value,
                        })
                      }
                      options={[
                        { value: ALL_MARKETING_FILTER, label: "ທຸກຫມວດ" },
                        ...(categoriesForMarketing.data?.data ?? []).map(
                          (category: CategoryDTO) => ({
                            value: category.id,
                            label: category.name,
                          }),
                        ),
                      ]}
                    />
                    <SimpleSelect
                      value={marketingProductId || ALL_MARKETING_FILTER}
                      onValueChange={(value) =>
                        updateSearch({
                          mappedProductId:
                            value === ALL_MARKETING_FILTER ? undefined : value,
                        })
                      }
                      options={[
                        { value: ALL_MARKETING_FILTER, label: "ທຸກສິນຄ້າ" },
                        ...(productsForMarketing.data?.data ?? []).map(
                          (product: ProductListItemDTO) => ({
                            value: product.id,
                            label: product.name,
                          }),
                        ),
                      ]}
                    />
                  </div>
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="rounded-full"
                      onClick={() =>
                        updateSearch({
                          platform: undefined,
                          campaign: undefined,
                          categoryId: undefined,
                          mappedProductId: undefined,
                        })
                      }
                    >
                      ລ້າງຕົວກອງ
                    </Button>
                  </div>
                </>
              ) : stockActive && stockSection === "movements" ? (
                <div className="flex flex-col gap-2">
                  <span className="text-muted-foreground text-sm">ເຫດການ</span>
                  <select
                    className="max-w-xs rounded-full border border-input bg-background px-3 py-1.5 text-sm"
                    value={stockReason ?? ""}
                    onChange={(e) => {
                      updateSearch({
                        reason: (e.target.value || undefined) as
                          | StockMovementsQueryDTO["reason"]
                          | undefined,
                      });
                      setStockOffset(0);
                    }}
                  >
                    <option value="">ທຸກເຫດການ</option>
                    <option value="order_paid">ຫຼຸດອໍເດີ້</option>
                    <option value="order_cancelled">ຍົກເລີກ</option>
                    <option value="order_returned">ຕີກັບ</option>
                    <option value="manual_adjust">ປັບມື</option>
                    <option value="goods_receipt">ຮັບເຂົ້າ lot</option>
                    <option value="goods_receipt_void">ຍົກເລີກຮັບເຂົ້າ</option>
                    <option value="import_order_receive">ຮັບນຳເຂົ້າ</option>
                  </select>
                </div>
              ) : stockActive && stockSection === "aging" ? (
                <div className="flex flex-col gap-2">
                  <span className="text-muted-foreground text-sm">
                    ຄ້າງຢ່າງໜ້ອຍ (ວັນ)
                  </span>
                  <Input
                    type="number"
                    min={1}
                    max={3650}
                    value={agingMinDays}
                    onChange={(e) => {
                      const v = Math.trunc(Number(e.target.value));
                      if (Number.isFinite(v) && v >= 1) {
                        updateSearch({ agingMinDays: Math.min(v, 3650) });
                      }
                    }}
                    className="max-w-48"
                  />
                </div>
              ) : stockActive && stockSection === "dead" ? (
                <DeadStockSettingsPanel
                  settings={deadStockSettings.data}
                  isLoading={deadStockSettings.isLoading}
                  isSaving={updateDeadStockSettings.isPending}
                  onSave={async (input) => {
                    const saved =
                      await updateDeadStockSettings.mutateAsync(input);
                    setDeadStockLookbackDays(saved.lookbackDays);
                    setDeadStockMinDays(saved.minDays);
                  }}
                />
              ) : null}
            </div>
          }
        />

        <Tabs value={activeTab} className="gap-4">
          <TabsContent value="sales" className="flex flex-col gap-4">
            <ReportsSectionNav
              primary={{
                items: SALES_SECTIONS,
                value: salesSection,
                onChange: (v) =>
                  updateSearch({
                    section: v,
                    sub: v === "products" ? productSection : undefined,
                  }),
                columns: 2,
              }}
              secondary={
                salesSection === "products"
                  ? {
                    items: PRODUCT_SECTIONS,
                    value: productSection,
                    onChange: (v) =>
                      updateSearch({ sub: v as ProductSection }),
                    columns: 3,
                  }
                  : undefined
              }
            />

            {salesSection === "overview" ? (
              <>
                {summary.isError ? (
                  <ReportsQueryError
                    onRetry={() => summary.refetch()}
                    isRetrying={summary.isFetching}
                  />
                ) : (
                  <>
                    <SalesSummaryCards
                      summary={summary.data}
                      isLoading={summary.isLoading}
                    />
                    <p className="text-muted-foreground text-xs">
                      ຍອດຂາຍຢືນຢັນ = ອໍເດີ້ທີ່ຫຼຸດສະຕັອກແລ້ວ (ບໍ່ນັບ draft / cancelled)
                    </p>
                  </>
                )}
                {byDay.isError ? (
                  <ReportsQueryError
                    onRetry={() => byDay.refetch()}
                    isRetrying={byDay.isFetching}
                  />
                ) : (
                  <details className="group">
                    <summary className="flex cursor-pointer select-none list-none items-center gap-2 rounded-xl border border-border bg-muted/30 px-4 py-2.5 font-medium text-foreground text-sm hover:bg-muted/50 [&::-webkit-details-marker]:hidden">
                      <ChevronRight className="size-4 shrink-0 transition-transform duration-200 group-open:rotate-90" />
                      <span className="group-open:hidden">ເບິ່ງກຣາຟຕາມວັນ</span>
                      <span className="hidden group-open:inline">ເຊື່ອງກຣາຟ</span>
                    </summary>
                    <div className="mt-3">
                      <SalesChart
                        rows={byDay.data}
                        isLoading={byDay.isLoading}
                      />
                    </div>
                  </details>
                )}
              </>
            ) : null}

            {salesSection === "products" && productSection === "best" ? (
              products.isError ? (
                <ReportsQueryError
                  onRetry={() => products.refetch()}
                  isRetrying={products.isFetching}
                />
              ) : (
                <>
                  <ProductPerformanceTable
                    rows={products.data}
                    isLoading={products.isLoading}
                    variant="best"
                    embedded
                  />
                  <ReportShowMore
                    visible={canShowMore(
                      products.data?.length ?? 0,
                      productLimit,
                      REPORT_LIMITS.product.max,
                    )}
                    loading={products.isFetching}
                    onShowMore={() =>
                      setProductLimit((limit) =>
                        Math.min(
                          limit + REPORT_LIMITS.product.step,
                          REPORT_LIMITS.product.max,
                        ),
                      )
                    }
                  />
                </>
              )
            ) : null}
            {salesSection === "products" && productSection === "worst" ? (
              slowProducts.isError ? (
                <ReportsQueryError
                  onRetry={() => slowProducts.refetch()}
                  isRetrying={slowProducts.isFetching}
                />
              ) : (
                <>
                  <ProductPerformanceTable
                    rows={slowProducts.data}
                    isLoading={slowProducts.isLoading}
                    variant="worst"
                    embedded
                  />
                  <ReportShowMore
                    visible={canShowMore(
                      slowProducts.data?.length ?? 0,
                      productLimit,
                      REPORT_LIMITS.product.max,
                    )}
                    loading={slowProducts.isFetching}
                    onShowMore={() =>
                      setProductLimit((limit) =>
                        Math.min(
                          limit + REPORT_LIMITS.product.step,
                          REPORT_LIMITS.product.max,
                        ),
                      )
                    }
                  />
                </>
              )
            ) : null}
            {salesSection === "products" && productSection === "frequency" ? (
              salesFrequency.isError ? (
                <ReportsQueryError
                  onRetry={() => salesFrequency.refetch()}
                  isRetrying={salesFrequency.isFetching}
                />
              ) : (
                <>
                  <SalesFrequencyTable
                    rows={salesFrequency.data}
                    isLoading={salesFrequency.isLoading}
                    embedded
                  />
                  <ReportShowMore
                    visible={canShowMore(
                      salesFrequency.data?.length ?? 0,
                      frequencyLimit,
                      REPORT_LIMITS.frequency.max,
                    )}
                    loading={salesFrequency.isFetching}
                    onShowMore={() =>
                      setFrequencyLimit((limit) =>
                        Math.min(
                          limit + REPORT_LIMITS.frequency.step,
                          REPORT_LIMITS.frequency.max,
                        ),
                      )
                    }
                  />
                </>
              )
            ) : null}
          </TabsContent>

          <TabsContent value="profit" className="flex flex-col gap-4">
            <ReportsSectionNav
              primary={{
                items: PROFIT_SECTIONS,
                value: profitSection,
                onChange: (v) => updateSearch({ section: v, sub: undefined }),
                columns: 3,
              }}
            />

            {profitSection === "overview" ? (
              <>
                {profitSummary.isError ? (
                  <ReportsQueryError
                    onRetry={() => profitSummary.refetch()}
                    isRetrying={profitSummary.isFetching}
                  />
                ) : (
                  <>
                    <ProfitSummaryCards
                      summary={profitSummary.data}
                      isLoading={profitSummary.isLoading}
                    />
                    <p className="text-muted-foreground text-xs">
                      ກຳໄລ = ຍອດຂາຍ − ຕົ້ນທຶນ (COGS) − ຄ່າໃຊ້ຈ່າຍ
                    </p>
                  </>
                )}
                <ProfitExpenseBreakdown
                  summary={profitSummary.data}
                  isLoading={profitSummary.isLoading}
                  isError={profitSummary.isError}
                  onRetry={() => profitSummary.refetch()}
                  isRetrying={profitSummary.isFetching}
                />
                {profitByDay.isError ? (
                  <ReportsQueryError
                    onRetry={() => profitByDay.refetch()}
                    isRetrying={profitByDay.isFetching}
                  />
                ) : (
                  <details className="group">
                    <summary className="flex cursor-pointer select-none list-none items-center gap-2 rounded-xl border border-border bg-muted/30 px-4 py-2.5 font-medium text-foreground text-sm hover:bg-muted/50 [&::-webkit-details-marker]:hidden">
                      <ChevronRight className="size-4 shrink-0 transition-transform duration-200 group-open:rotate-90" />
                      <span className="group-open:hidden">ເບິ່ງກຣາຟກຳໄລ</span>
                      <span className="hidden group-open:inline">ເຊື່ອງກຣາຟ</span>
                    </summary>
                    <div className="mt-3">
                      <ProfitChart
                        rows={profitByDay.data}
                        isLoading={profitByDay.isLoading}
                      />
                    </div>
                  </details>
                )}
              </>
            ) : null}

            {profitSection === "marketing" ? (
              <>
                {marketingRoi.isError ? (
                  <ReportsQueryError
                    onRetry={() => marketingRoi.refetch()}
                    isRetrying={marketingRoi.isFetching}
                  />
                ) : (
                  <>
                    <MarketingRoiTable
                      result={marketingRoi.data}
                      isLoading={marketingRoi.isLoading}
                    />
                    <ReportShowMore
                      visible={canShowMore(
                        marketingRoi.data?.data.length ?? 0,
                        marketingRoiLimit,
                        REPORT_LIMITS.marketingRoi.max,
                      )}
                      loading={marketingRoi.isFetching}
                      onShowMore={() =>
                        setMarketingRoiLimit((limit) =>
                          Math.min(
                            limit + REPORT_LIMITS.marketingRoi.step,
                            REPORT_LIMITS.marketingRoi.max,
                          ),
                        )
                      }
                    />
                  </>
                )}
                {profitCampaigns.isError ? (
                  <ReportsQueryError
                    onRetry={() => profitCampaigns.refetch()}
                    isRetrying={profitCampaigns.isFetching}
                  />
                ) : (
                  <ProfitCampaignSummaryTable
                    rows={profitCampaigns.data}
                    isLoading={profitCampaigns.isLoading}
                  />
                )}
              </>
            ) : null}

            {profitSection === "products" ? (
              profitByProduct.isError ? (
                <ReportsQueryError
                  onRetry={() => profitByProduct.refetch()}
                  isRetrying={profitByProduct.isFetching}
                />
              ) : (
                <>
                  <ProfitByProductTable
                    rows={profitByProduct.data}
                    isLoading={profitByProduct.isLoading}
                  />
                  <ReportShowMore
                    visible={canShowMore(
                      profitByProduct.data?.length ?? 0,
                      profitProductLimit,
                      REPORT_LIMITS.profitProduct.max,
                    )}
                    loading={profitByProduct.isFetching}
                    onShowMore={() =>
                      setProfitProductLimit((limit) =>
                        Math.min(
                          limit + REPORT_LIMITS.profitProduct.step,
                          REPORT_LIMITS.profitProduct.max,
                        ),
                      )
                    }
                  />
                </>
              )
            ) : null}
          </TabsContent>

          <TabsContent value="stock" className="flex flex-col gap-4">
            <StockOverviewStrip
              valuation={stockValuation.data}
              valuationLoading={stockValuation.isLoading}
              agingCount={agingStock.data?.length}
              agingLoading={agingStock.isLoading}
              deadCount={deadStock.data?.data.length}
              deadLoading={deadStock.isLoading || deadStockSettings.isLoading}
              onGoValuation={() =>
                updateSearch({ section: "valuation", sub: undefined })
              }
              onGoAging={() =>
                updateSearch({ section: "aging", sub: undefined })
              }
              onGoDead={() => updateSearch({ section: "dead", sub: undefined })}
            />
            <ReportsSectionNav
              primary={{
                items: STOCK_SECTIONS,
                value: stockSection,
                onChange: (v) => updateSearch({ section: v, sub: undefined }),
                columns: 4,
              }}
            />

            {stockSection === "aging" ? (
              agingStock.isError ? (
                <ReportsQueryError
                  onRetry={() => agingStock.refetch()}
                  isRetrying={agingStock.isFetching}
                />
              ) : (
                <>
                  <AgingStockTable
                    rows={agingStock.data}
                    isLoading={agingStock.isLoading}
                    minDays={agingMinDays}
                    onMinDaysChange={(v) => updateSearch({ agingMinDays: v })}
                    embedded
                  />
                  <ReportShowMore
                    visible={canShowMore(
                      agingStock.data?.length ?? 0,
                      agingLimit,
                      REPORT_LIMITS.aging.max,
                    )}
                    loading={agingStock.isFetching}
                    onShowMore={() =>
                      setAgingLimit((limit) =>
                        Math.min(
                          limit + REPORT_LIMITS.aging.step,
                          REPORT_LIMITS.aging.max,
                        ),
                      )
                    }
                  />
                </>
              )
            ) : null}

            {stockSection === "dead" ? (
              <>
                {deadStock.isError ? (
                  <ReportsQueryError
                    onRetry={() => deadStock.refetch()}
                    isRetrying={deadStock.isFetching}
                  />
                ) : (
                  <>
                    <DeadStockTable
                      result={deadStock.data}
                      isLoading={deadStock.isLoading}
                      exporting={deadStockExporting}
                      onExport={async () => {
                        if (
                          resolvedDeadStockLookback == null ||
                          resolvedDeadStockMinDays == null
                        ) {
                          return;
                        }
                        setDeadStockExporting(true);
                        try {
                          await reportsApi.downloadDeadStockCsv({
                            lookbackDays: resolvedDeadStockLookback,
                            minDays: resolvedDeadStockMinDays,
                            limit: deadStockLimit,
                            ...productFilter,
                          });
                          toast.success("ດາວໂຫລດ CSV ສຳເລັດ");
                        } catch (e) {
                          toast.error("ສົ່ງອອກ CSV ບໍ່ສຳເລັດ", {
                            description:
                              e instanceof Error ? e.message : undefined,
                          });
                        } finally {
                          setDeadStockExporting(false);
                        }
                      }}
                    />
                    <ReportShowMore
                      visible={canShowMore(
                        deadStock.data?.data.length ?? 0,
                        deadStockLimit,
                        REPORT_LIMITS.deadStock.max,
                      )}
                      loading={deadStock.isFetching}
                      onShowMore={() =>
                        setDeadStockLimit((limit) =>
                          Math.min(
                            limit + REPORT_LIMITS.deadStock.step,
                            REPORT_LIMITS.deadStock.max,
                          ),
                        )
                      }
                    />
                  </>
                )}
              </>
            ) : null}

            {stockSection === "movements" ? (
              <>
                {stock.isError ? (
                  <ReportsQueryError
                    onRetry={() => stock.refetch()}
                    isRetrying={stock.isFetching}
                  />
                ) : (
                  <StockMovementsTable
                    rows={stock.data?.data}
                    isLoading={stock.isLoading}
                  />
                )}
                {stockTotal > STOCK_PAGE_SIZE ? (
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-muted-foreground text-sm">
                      {stockOffset + 1}–
                      {Math.min(stockOffset + STOCK_PAGE_SIZE, stockTotal)} ຈາກ{" "}
                      {stockTotal}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-full"
                        disabled={!stockHasPrev}
                        onClick={() =>
                          setStockOffset((o) =>
                            Math.max(0, o - STOCK_PAGE_SIZE),
                          )
                        }
                      >
                        ກັບ
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-full"
                        disabled={!stockHasMore}
                        onClick={() =>
                          setStockOffset((o) => o + STOCK_PAGE_SIZE)
                        }
                      >
                        ຕໍ່
                      </Button>
                    </div>
                  </div>
                ) : null}
              </>
            ) : null}

            {stockSection === "valuation" ? (
              stockValuation.isError ? (
                <ReportsQueryError
                  onRetry={() => stockValuation.refetch()}
                  isRetrying={stockValuation.isFetching}
                />
              ) : (
                <>
                  <StockValuationSummaryCards
                    summary={stockValuation.data}
                    isLoading={stockValuation.isLoading}
                  />
                  <StockValuationTable
                    rows={stockValuation.data?.topVariants}
                    isLoading={stockValuation.isLoading}
                    embedded
                  />
                  <ReportShowMore
                    visible={canShowMore(
                      stockValuation.data?.topVariants.length ?? 0,
                      valuationLimit,
                      REPORT_LIMITS.valuation.max,
                    )}
                    loading={stockValuation.isFetching}
                    onShowMore={() =>
                      setValuationLimit((limit) =>
                        Math.min(
                          limit + REPORT_LIMITS.valuation.step,
                          REPORT_LIMITS.valuation.max,
                        ),
                      )
                    }
                  />
                </>
              )
            ) : null}
          </TabsContent>

          <TabsContent value="shipping" className="flex flex-col gap-4">
            {!shippingPerformance.isError ? (
              <ShippingOverviewStrip
                result={shippingPerformance.data}
                isLoading={shippingPerformance.isLoading}
              />
            ) : null}
            {shippingPerformance.isError ? (
              <ReportsQueryError
                onRetry={() => shippingPerformance.refetch()}
                isRetrying={shippingPerformance.isFetching}
              />
            ) : (
              <>
                <ShippingPerformanceTable
                  result={shippingPerformance.data}
                  isLoading={shippingPerformance.isLoading}
                />
                <ReportShowMore
                  visible={canShowMore(
                    shippingPerformance.data?.data.length ?? 0,
                    shippingLimit,
                    REPORT_LIMITS.shipping.max,
                  )}
                  loading={shippingPerformance.isFetching}
                  onShowMore={() =>
                    setShippingLimit((limit) =>
                      Math.min(
                        limit + REPORT_LIMITS.shipping.step,
                        REPORT_LIMITS.shipping.max,
                      ),
                    )
                  }
                />
              </>
            )}
          </TabsContent>

          <TabsContent value="customers" className="flex flex-col gap-4">
            <ReportsSectionNav
              primary={{
                items: CUSTOMER_SECTIONS,
                value: customerSection,
                onChange: (v) =>
                  updateSearch({
                    section: v as CustomerSection,
                    customerProvince:
                      v === "geo" ? customerProvince || undefined : undefined,
                    customerDistrict: undefined,
                  }),
                columns: 3,
              }}
              secondary={
                customerSection === "watch"
                  ? {
                    items: CUSTOMER_WATCH_KINDS.map((item) => ({
                      value: `${item.value}:${item.minCodReturns}`,
                      label: item.label,
                      description: item.description,
                    })),
                    value: activeWatchKindKey,
                    onChange: (v) => {
                      const [kind, minRaw] = v.split(":");
                      updateSearch({
                        watchKind: kind as CustomerWatchlistKind,
                        minCodReturns: Number(minRaw) || 1,
                      });
                    },
                    columns: 2,
                    label: "ປະເພດລາຍການ",
                    compact: true,
                  }
                  : undefined
              }
            />

            {customerSection === "overview" ? (
              customerSummary.isError ? (
                <ReportsQueryError
                  onRetry={() => customerSummary.refetch()}
                  isRetrying={customerSummary.isFetching}
                />
              ) : (
                <>
                  <CustomerReportsOverviewStrip
                    summary={customerSummary.data}
                    isLoading={customerSummary.isLoading}
                  />
                  {customerRiskSummary.isError ? (
                    <ReportsQueryError
                      onRetry={() => customerRiskSummary.refetch()}
                      isRetrying={customerRiskSummary.isFetching}
                    />
                  ) : (
                    <CustomerRiskTierChart
                      summary={customerRiskSummary.data}
                      isLoading={customerRiskSummary.isLoading}
                    />
                  )}
                </>
              )
            ) : null}

            {customerSection === "top" ? (
              customerTop.isError ? (
                <ReportsQueryError
                  onRetry={() => customerTop.refetch()}
                  isRetrying={customerTop.isFetching}
                />
              ) : (
                <>
                  <CustomerTopTable
                    result={customerTop.data}
                    isLoading={customerTop.isLoading}
                  />
                  <ReportShowMore
                    visible={canShowMore(
                      customerTop.data?.data.length ?? 0,
                      customerTopLimit,
                      REPORT_LIMITS.customerTop.max,
                    )}
                    loading={customerTop.isFetching}
                    onShowMore={() =>
                      setCustomerTopLimit((limit) =>
                        Math.min(
                          limit + REPORT_LIMITS.customerTop.step,
                          REPORT_LIMITS.customerTop.max,
                        ),
                      )
                    }
                  />
                </>
              )
            ) : null}

            {customerSection === "geo" ? (
              customerProvince ? (
                customerGeoDistrict.isError ? (
                  <ReportsQueryError
                    onRetry={() => customerGeoDistrict.refetch()}
                    isRetrying={customerGeoDistrict.isFetching}
                  />
                ) : (
                  <CustomerGeoTable
                    result={customerGeoDistrict.data}
                    isLoading={customerGeoDistrict.isLoading}
                    selectedProvince={customerProvince}
                    onClearProvince={() =>
                      updateSearch({
                        customerProvince: undefined,
                        customerDistrict: undefined,
                      })
                    }
                  />
                )
              ) : customerGeoProvince.isError ? (
                <ReportsQueryError
                  onRetry={() => customerGeoProvince.refetch()}
                  isRetrying={customerGeoProvince.isFetching}
                />
              ) : (
                <>
                  <CustomerGeoChart
                    result={customerGeoProvince.data}
                    isLoading={customerGeoProvince.isLoading}
                  />
                  <CustomerGeoTable
                    result={customerGeoProvince.data}
                    isLoading={customerGeoProvince.isLoading}
                    onProvinceSelect={(province) =>
                      updateSearch({ customerProvince: province })
                    }
                  />
                </>
              )
            ) : null}

            {customerSection === "watch" ? (
              customerWatchlist.isError ? (
                <ReportsQueryError
                  onRetry={() => customerWatchlist.refetch()}
                  isRetrying={customerWatchlist.isFetching}
                />
              ) : (
                <CustomerWatchlistTable
                  result={customerWatchlist.data}
                  isLoading={customerWatchlist.isLoading}
                />
              )
            ) : null}

            {customerSection === "affinity" ? (
              customerAffinity.isError ? (
                <ReportsQueryError
                  onRetry={() => customerAffinity.refetch()}
                  isRetrying={customerAffinity.isFetching}
                />
              ) : (
                <>
                  <CustomerAffinityChart
                    result={customerAffinity.data}
                    isLoading={customerAffinity.isLoading}
                  />
                  <CustomerAffinityTable
                    result={customerAffinity.data}
                    isLoading={customerAffinity.isLoading}
                  />
                </>
              )
            ) : null}

            {customerSection === "cohorts" ? (
              <>
                <div className="brand-card flex flex-wrap items-center gap-3 p-3 sm:p-4">
                  <span className="font-medium text-sm">ຈຳນວນ cohort</span>
                  <SimpleSelect
                    value={String(cohortMonths)}
                    onValueChange={(value) =>
                      updateSearch({ cohortMonths: Number(value) })
                    }
                    options={[
                      { value: "3", label: "3 ເດືອນ" },
                      { value: "6", label: "6 ເດືອນ" },
                      { value: "9", label: "9 ເດືອນ" },
                      { value: "12", label: "12 ເດືອນ" },
                    ]}
                    className="w-[140px]"
                  />
                </div>
                {customerCohorts.isError ? (
                  <ReportsQueryError
                    onRetry={() => customerCohorts.refetch()}
                    isRetrying={customerCohorts.isFetching}
                  />
                ) : (
                  <>
                    <CustomerCohortChart
                      result={customerCohorts.data}
                      isLoading={customerCohorts.isLoading}
                    />
                    <CustomerCohortTable
                      result={customerCohorts.data}
                      isLoading={customerCohorts.isLoading}
                    />
                  </>
                )}
              </>
            ) : null}
          </TabsContent>

          <TabsContent value="custom_jobs" className="flex flex-col gap-4">
            {customJobsSummary.isError ? (
              <ReportsQueryError
                onRetry={() => customJobsSummary.refetch()}
                isRetrying={customJobsSummary.isFetching}
              />
            ) : (
              <CustomJobsReportPanel
                data={customJobsSummary.data}
                isLoading={customJobsSummary.isLoading}
              />
            )}
          </TabsContent>
        </Tabs>
      </Main>
    </>
  );
}
